import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { Piano } from '../lib/tipi'

export type StatoAbbonamento = 'nessuno' | 'trial' | 'attivo' | 'cancellato' | 'scaduto'

interface RispostaStato {
  piano: Piano
  stato: StatoAbbonamento
  trial_fine: string | null
  giorni_trial_rimanenti: number | null
  abbonamento_fine: string | null
  ha_abbonamento: boolean
  ha_customer: boolean
}

/**
 * Hook per leggere lo stato dell'abbonamento corrente + mutation per
 * le azioni: avvia checkout, apri portale, cancella trial.
 */
export function useAbbonamento() {
  const queryClient = useQueryClient()

  const stato = useQuery({
    queryKey: ['abbonamento-stato'],
    queryFn: async () =>
      (await api.get<{ dati: RispostaStato }>('/stripe/stato')).data.dati!,
    // Polling lento ogni 30s (così se il webhook aggiorna il piano,
    // l'utente lo vede senza ricaricare)
    refetchInterval: 30_000,
  })

  const avviaCheckout = useMutation({
    mutationFn: async (piano: 'pro' | 'team') =>
      (await api.post<{ dati: { checkout_url: string } }>('/stripe/checkout', { piano })).data.dati!,
  })

  const apriPortale = useMutation({
    mutationFn: async () =>
      (await api.post<{ dati: { portale_url: string } }>('/stripe/portale')).data.dati!,
  })

  const cancellaTrial = useMutation({
    mutationFn: async () => api.post('/stripe/cancella-trial'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['abbonamento-stato'] })
      queryClient.invalidateQueries({ queryKey: ['profilo'] })
    },
  })

  return {
    stato: stato.data,
    isLoading: stato.isLoading,
    error: stato.error,
    refetch: stato.refetch,
    avviaCheckout,
    apriPortale,
    cancellaTrial,
  }
}
